import { useMemo } from 'react'

import { Grid } from '@mui/material'
import { Skeleton } from '@mui/material'
import { useFormContext } from 'react-hook-form'
import { toast } from 'react-toastify'

import {
  ControlledSelector,
  ControlledTextField,
  ControlledDatePicker,
  CardTitle,
  ControlledCheckbox,
} from 'components/common'
import {
  useGetVehicleTypesQuery,
  CreateAndUpdateVehicleDtoInput,
  SortEnumType,
  useGetVehicleBodyTypesQuery,
  BodyType,
} from 'generated/graphql'

const DriverVehicleDetails = () => {
  const spacing = 3

  const {
    control,
    formState: { errors },
  } = useFormContext<CreateAndUpdateVehicleDtoInput>()

  // queries
  const { loading: vehicleTypeLoading, data: vehicleTypeData } = useGetVehicleTypesQuery({
    variables: {
      order: {
        sequenceOrder: SortEnumType.Asc,
      },
    },
    onError: (error) => {
      error.graphQLErrors.map((e) => toast.error(e.message))
    },
  })

  const { loading: vehicleBodyTypeLoading, data: vehicleBodyTypeData } =
    useGetVehicleBodyTypesQuery()

  const vehicleTypesValues = useMemo(() => {
    if (!vehicleTypeLoading && !!vehicleTypeData) {
      return vehicleTypeData.vehicleTypes.map((vt) => ({
        value: Number(vt.id),
        label: vt.name,
      }))
    }
    return []
  }, [vehicleTypeData, vehicleTypeLoading])

  const bodyTypes = useMemo(() => {
    if (!vehicleBodyTypeLoading && !!vehicleBodyTypeData) {
      const types = vehicleBodyTypeData.vehicleBodyTypes.map((vt) => ({
        value: vt.bodyType,
        label: vt.description,
      }))

      return types.filter((t) => t.value !== BodyType.NotSpecified)
    }
    return []
  }, [vehicleBodyTypeData, vehicleBodyTypeLoading])

  return (
    <>
      <CardTitle>Driver Vehicle Details</CardTitle>

      {/* Registration Number */}
      <Grid container spacing={spacing}>
        <Grid item xs={12} sm={6}>
          <ControlledTextField
            control={control}
            name='registrationNumber'
            label='Registration No'
            required
            error={!!errors.registrationNumber}
            helperText={errors.registrationNumber?.message}
          />
        </Grid>

        {/* Vehicle Types */}
        <Grid item xs={12} sm={6}>
          {vehicleTypeLoading ? (
            <Skeleton animation='wave' />
          ) : (
            <ControlledSelector
              name='vehicleTypeId'
              label='Vehicle Type'
              options={vehicleTypesValues}
              control={control}
              required
              error={!!errors.vehicleTypeId}
              helperText={errors.vehicleTypeId?.message}
            />
          )}
        </Grid>

        {/* Body Types */}
        <Grid item xs={12} sm={6}>
          {vehicleBodyTypeLoading ? (
            <Skeleton animation='wave' />
          ) : (
            <ControlledSelector
              name='bodyType'
              label='Body Type'
              options={bodyTypes}
              control={control}
              required
              error={!!errors.bodyType}
              helperText={errors.bodyType?.message}
            />
          )}
        </Grid>

        {/* Has tail lift */}
        <Grid item xs={12} sm={6}>
          <ControlledCheckbox name='hasTailLift' control={control} label='Tail Lift' />
        </Grid>

        {/* Manufacturer */}
        <Grid item xs={12} sm={6}>
          <ControlledTextField
            control={control}
            name='manufacturer'
            label='Manufacturer'
            error={!!errors.manufacturer}
            helperText={errors.manufacturer?.message}
          />
        </Grid>

        {/* Model */}
        <Grid item xs={12} sm={6}>
          <ControlledTextField
            control={control}
            name='model'
            label='Model'
            error={!!errors.model}
            helperText={errors.model?.message}
          />
        </Grid>

        {/* Colour */}
        <Grid item xs={12} sm={6}>
          <ControlledTextField
            name='colour'
            label='Colour'
            control={control}
            error={!!errors.colour}
            helperText={errors.colour?.message}
          />
        </Grid>

        {/* Date of Manufacture */}
        <Grid item xs={12} sm={6}>
          <ControlledDatePicker
            control={control}
            name='dateOfManufacture'
            label='Date of Manufacture'
            error={!!errors.dateOfManufacture}
            helperText={errors.dateOfManufacture?.message}
          />
        </Grid>

        {/* Is Default */}
        <Grid item xs={12} sm={6}>
          <ControlledCheckbox
            name='isDefault'
            defaultValue={null}
            control={control}
            label='Default Vehicle'
          />
        </Grid>
      </Grid>
    </>
  )
}

export default DriverVehicleDetails
